
import { useState, useEffect } from 'react';
import Button from '../base/Button';

interface CartItem {
  id: string;
  title: string;
  price: number;
  digitalPrice: number;
  format: 'physical' | 'digital';
  quantity: number;
  image: string;
}

interface ShoppingCartProps {
  isOpen: boolean;
  onClose: () => void;
}

interface DiscountCode {
  code: string;
  discount: number;
  type: 'percentage' | 'fixed';
  description: string;
}

interface ShippingInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

const availableDiscounts: DiscountCode[] = [
  {
    code: 'WELCOME15',
    discount: 15,
    type: 'percentage',
    description: 'First-time reader discount - 15% off'
  }
];

export default function ShoppingCart({ isOpen, onClose }: ShoppingCartProps) {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState<DiscountCode | null>(null);
  const [discountError, setDiscountError] = useState('');
  const [showShippingForm, setShowShippingForm] = useState(false);
  const [shippingInfo, setShippingInfo] = useState<ShippingInfo>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States'
  });
  const [shippingErrors, setShippingErrors] = useState<Partial<ShippingInfo>>({});

  useEffect(() => {
    // Load cart from localStorage
    const savedCart = localStorage.getItem('bookCart');
    if (savedCart) {
      setCartItems(JSON.parse(savedCart));
    }

    // Load applied discount from localStorage
    const savedDiscount = localStorage.getItem('appliedDiscount');
    if (savedDiscount) {
      setAppliedDiscount(JSON.parse(savedDiscount));
    }

    // Load shipping info from localStorage
    const savedShipping = localStorage.getItem('shippingInfo');
    if (savedShipping) {
      setShippingInfo(JSON.parse(savedShipping));
    }
  }, [isOpen]);

  const updateCart = (items: CartItem[]) => {
    setCartItems(items);
    localStorage.setItem('bookCart', JSON.stringify(items));
  };

  const removeItem = (id: string, format: string) => {
    const updatedItems = cartItems.filter(item => !(item.id === id && item.format === format));
    updateCart(updatedItems);
  };

  const updateQuantity = (id: string, format: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(id, format);
      return;
    }
    
    const updatedItems = cartItems.map(item => 
      item.id === id && item.format === format 
        ? { ...item, quantity }
        : item
    );
    updateCart(updatedItems);
  };

  const getSubtotal = () => {
    return cartItems.reduce((total, item) => {
      const price = item.format === 'digital' ? item.digitalPrice : item.price;
      return total + (price * item.quantity);
    }, 0);
  };

  const getDiscountAmount = () => {
    if (!appliedDiscount) return 0;
    
    const subtotal = getSubtotal();
    if (appliedDiscount.type === 'percentage') {
      return subtotal * (appliedDiscount.discount / 100);
    } else {
      return Math.min(appliedDiscount.discount, subtotal);
    }
  };

  const getShippingCost = () => {
    // Only charge shipping if there are physical items
    const hasPhysical = cartItems.some(item => item.format === 'physical');
    return hasPhysical ? 8.99 : 0;
  };

  const getTotalPrice = () => {
    const subtotal = getSubtotal();
    const discount = getDiscountAmount();
    const shipping = getShippingCost();
    return Math.max(0, subtotal - discount + shipping);
  };

  const applyDiscountCode = () => {
    setDiscountError('');
    
    if (!discountCode.trim()) {
      setDiscountError('Please enter a discount code');
      return;
    }

    const discount = availableDiscounts.find(
      d => d.code.toLowerCase() === discountCode.trim().toLowerCase()
    );

    if (!discount) {
      setDiscountError('Invalid discount code');
      return;
    }

    if (appliedDiscount && appliedDiscount.code === discount.code) {
      setDiscountError('This discount code is already applied');
      return;
    }

    setAppliedDiscount(discount);
    localStorage.setItem('appliedDiscount', JSON.stringify(discount));
    setDiscountCode('');
    setDiscountError('');
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
    localStorage.removeItem('appliedDiscount');
    setDiscountError('');
  };

  const validateShippingInfo = () => {
    const errors: Partial<ShippingInfo> = {};
    
    // Email is always required for all orders
    if (!shippingInfo.email.trim()) errors.email = 'Email is required';
    if (shippingInfo.email && !/\S+@\S+\.\S+/.test(shippingInfo.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    // Only validate shipping fields if there are physical items
    if (hasPhysicalItems()) {
      if (!shippingInfo.firstName.trim()) errors.firstName = 'First name is required';
      if (!shippingInfo.lastName.trim()) errors.lastName = 'Last name is required';
      if (!shippingInfo.address.trim()) errors.address = 'Address is required';
      if (!shippingInfo.city.trim()) errors.city = 'City is required';
      if (!shippingInfo.state.trim()) errors.state = 'State is required';
      if (!shippingInfo.zipCode.trim()) errors.zipCode = 'ZIP code is required';
    }

    setShippingErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const updateShippingInfo = (field: keyof ShippingInfo, value: string) => {
    const updatedInfo = { ...shippingInfo, [field]: value };
    setShippingInfo(updatedInfo);
    localStorage.setItem('shippingInfo', JSON.stringify(updatedInfo));
    
    // Clear error for this field
    if (shippingErrors[field]) {
      setShippingErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const hasPhysicalItems = () => {
    // Only require shipping information if there are physical items
    return cartItems.some(item => item.format === 'physical');
  };

  const proceedToCheckout = () => {
    // Always check if email is provided first
    if (!shippingInfo.email.trim()) {
      setShippingErrors({ email: 'Email is required for all orders' });
      setShowShippingForm(true);
      return;
    }

    if (hasPhysicalItems() && !showShippingForm) {
      setShowShippingForm(true);
      return;
    }
    
    if (hasPhysicalItems() && !validateShippingInfo()) {
      return;
    }
    
    // For digital-only orders, validate email
    if (!hasPhysicalItems() && !validateShippingInfo()) {
      setShowShippingForm(true);
      return;
    }
    
    handleCheckout();
  };

  const handleCheckout = async () => {
    if (cartItems.length === 0) return;
    
    setIsCheckingOut(true);
    
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-book-checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cartItems,
          customerEmail: shippingInfo.email,
          customerName: `${shippingInfo.firstName} ${shippingInfo.lastName}`,
          shippingAddress: hasPhysicalItems() ? shippingInfo : null,
          discountCode: appliedDiscount?.code || null,
          discountAmount: getDiscountAmount()
        }),
      });

      const result = await response.json();
      
      if (result.success && result.checkoutUrl) {
        // Redirect to Stripe checkout
        window.location.href = result.checkoutUrl;
      } else {
        throw new Error(result.error || 'Failed to create checkout session');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Failed to process checkout. Please try again.');
    } finally {
      setIsCheckingOut(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-end z-50">
      <div className="bg-white h-full w-full max-w-md shadow-xl overflow-y-auto">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-800">
              {showShippingForm ? 'Shipping Information' : 'Shopping Cart'}
            </h2>
            <button 
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
          {showShippingForm && (
            <button
              onClick={() => setShowShippingForm(false)}
              className="flex items-center text-slate-600 hover:text-slate-800 mt-2 cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Back to Cart
            </button>
          )}
        </div>

        <div className="flex-1 p-6">
          {!showShippingForm ? (
            <>
              {cartItems.length === 0 ? (
                <div className="text-center py-12">
                  <i className="ri-shopping-cart-line text-6xl text-slate-300 mb-4"></i>
                  <h3 className="text-lg font-semibold text-slate-600 mb-2">Your cart is empty</h3>
                  <p className="text-slate-500 mb-6">Add some books to get started</p>
                  <Button onClick={onClose} variant="outline">
                    Continue Shopping
                  </Button>
                </div>
              ) : (
                <>
                  <div className="space-y-4 mb-6">
                    {cartItems.map((item, index) => (
                      <div key={`${item.id}-${item.format}-${index}`} className="flex items-center space-x-4 p-4 border border-slate-200 rounded-lg">
                        <img 
                          src={item.image} 
                          alt={item.title}
                          className="w-16 h-20 object-contain bg-slate-50 rounded"
                        />
                        <div className="flex-1">
                          <h4 className="font-semibold text-slate-800 text-sm">{item.title}</h4>
                          <p className="text-xs text-slate-500 mb-2">
                            {item.format === 'digital' ? 'Digital PDF' : 'Paperback'}
                          </p>
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-slate-800">
                              ${item.format === 'digital' ? item.digitalPrice : item.price}
                            </span>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => updateQuantity(item.id, item.format, item.quantity - 1)}
                                className="w-6 h-6 flex items-center justify-center bg-slate-100 rounded cursor-pointer hover:bg-slate-200"
                              >
                                <i className="ri-subtract-line text-xs"></i>
                              </button>
                              <span className="text-sm font-medium w-8 text-center">{item.quantity}</span>
                              <button
                                onClick={() => updateQuantity(item.id, item.format, item.quantity + 1)}
                                className="w-6 h-6 flex items-center justify-center bg-slate-100 rounded cursor-pointer hover:bg-slate-200"
                              >
                                <i className="ri-add-line text-xs"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => removeItem(item.id, item.format)}
                          className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-red-500 cursor-pointer"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Discount Code Section */}
                  <div className="border-t border-slate-200 pt-6 mb-6">
                    <h3 className="text-sm font-semibold text-slate-800 mb-3">Discount Code</h3>
                    
                    {appliedDiscount ? (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-green-800">{appliedDiscount.code}</p>
                            <p className="text-xs text-green-600">{appliedDiscount.description}</p>
                          </div>
                          <button
                            onClick={removeDiscount}
                            className="text-green-600 hover:text-green-800 cursor-pointer"
                          >
                            <i className="ri-close-line text-sm"></i>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex space-x-2">
                          <input
                            type="text"
                            value={discountCode}
                            onChange={(e) => setDiscountCode(e.target.value)}
                            placeholder="Enter discount code"
                            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                            onKeyPress={(e) => e.key === 'Enter' && applyDiscountCode()}
                          />
                          <Button
                            onClick={applyDiscountCode}
                            variant="outline"
                            className="px-4 py-2 text-sm whitespace-nowrap"
                          >
                            Apply
                          </Button>
                        </div>
                        {discountError && (
                          <p className="text-red-500 text-xs">{discountError}</p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="border-t border-slate-200 pt-6">
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">Subtotal:</span>
                        <span className="text-slate-800">${getSubtotal().toFixed(2)}</span>
                      </div>
                      
                      {appliedDiscount && (
                        <div className="flex justify-between items-center text-green-600">
                          <span>Discount ({appliedDiscount.code}):</span>
                          <span>-${getDiscountAmount().toFixed(2)}</span>
                        </div>
                      )}
                      
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">Shipping:</span>
                        <span className="text-slate-800">
                          {getShippingCost() > 0 ? `$${getShippingCost().toFixed(2)}` : 'Free'}
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center border-t border-slate-200 pt-2">
                        <span className="text-lg font-bold text-slate-800">Total:</span>
                        <span className="text-2xl font-bold text-blue-600">${getTotalPrice().toFixed(2)}</span>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <Button 
                        onClick={proceedToCheckout}
                        disabled={isCheckingOut || cartItems.length === 0}
                        className="w-full bg-blue-800 hover:bg-blue-900"
                        size="lg"
                      >
                        {isCheckingOut ? 'Processing...' : hasPhysicalItems() && !showShippingForm ? 'Continue to Shipping' : 'Proceed to Checkout'}
                      </Button>
                      <Button 
                        onClick={onClose}
                        variant="outline"
                        className="w-full"
                      >
                        Continue Shopping
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </>
          ) : (
            /* Shipping Form */
            <div className="space-y-6">
              {!hasPhysicalItems() ? (
                /* Digital-only order - only email required */
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-4">Email for Digital Delivery</h3>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={shippingInfo.email}
                      onChange={(e) => updateShippingInfo('email', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        shippingErrors.email ? 'border-red-300' : 'border-slate-300'
                      }`}
                      placeholder="Enter your email for digital delivery"
                    />
                    {shippingErrors.email && (
                      <p className="text-red-500 text-xs mt-1">{shippingErrors.email}</p>
                    )}
                    <p className="text-xs text-slate-500 mt-2">
                      Your digital books will be delivered to this email address after payment.
                    </p>
                  </div>
                </div>
              ) : (
                /* Physical items - full shipping form */
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        value={shippingInfo.firstName}
                        onChange={(e) => updateShippingInfo('firstName', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          shippingErrors.firstName ? 'border-red-300' : 'border-slate-300'
                        }`}
                      />
                      {shippingErrors.firstName && (
                        <p className="text-red-500 text-xs mt-1">{shippingErrors.firstName}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        value={shippingInfo.lastName}
                        onChange={(e) => updateShippingInfo('lastName', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          shippingErrors.lastName ? 'border-red-300' : 'border-slate-300'
                        }`}
                      />
                      {shippingErrors.lastName && (
                        <p className="text-red-500 text-xs mt-1">{shippingErrors.lastName}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={shippingInfo.email}
                      onChange={(e) => updateShippingInfo('email', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        shippingErrors.email ? 'border-red-300' : 'border-slate-300'
                      }`}
                    />
                    {shippingErrors.email && (
                      <p className="text-red-500 text-xs mt-1">{shippingErrors.email}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={shippingInfo.phone}
                      onChange={(e) => updateShippingInfo('phone', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Street Address *
                    </label>
                    <input
                      type="text"
                      value={shippingInfo.address}
                      onChange={(e) => updateShippingInfo('address', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        shippingErrors.address ? 'border-red-300' : 'border-slate-300'
                      }`}
                    />
                    {shippingErrors.address && (
                      <p className="text-red-500 text-xs mt-1">{shippingErrors.address}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        City *
                      </label>
                      <input
                        type="text"
                        value={shippingInfo.city}
                        onChange={(e) => updateShippingInfo('city', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          shippingErrors.city ? 'border-red-300' : 'border-slate-300'
                        }`}
                      />
                      {shippingErrors.city && (
                        <p className="text-red-500 text-xs mt-1">{shippingErrors.city}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        State *
                      </label>
                      <input
                        type="text"
                        value={shippingInfo.state}
                        onChange={(e) => updateShippingInfo('state', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          shippingErrors.state ? 'border-red-300' : 'border-slate-300'
                        }`}
                      />
                      {shippingErrors.state && (
                        <p className="text-red-500 text-xs mt-1">{shippingErrors.state}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        ZIP Code *
                      </label>
                      <input
                        type="text"
                        value={shippingInfo.zipCode}
                        onChange={(e) => updateShippingInfo('zipCode', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          shippingErrors.zipCode ? 'border-red-300' : 'border-slate-300'
                        }`}
                      />
                      {shippingErrors.zipCode && (
                        <p className="text-red-500 text-xs mt-1">{shippingErrors.zipCode}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Country
                      </label>
                      <select
                        value={shippingInfo.country}
                        onChange={(e) => updateShippingInfo('country', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
                      >
                        <option value="United States">United States</option>
                        <option value="Canada">Canada</option>
                        <option value="United Kingdom">United Kingdom</option>
                        <option value="Australia">Australia</option>
                      </select>
                    </div>
                  </div>
                </>
              )}

              <div className="border-t border-slate-200 pt-6">
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Subtotal:</span>
                    <span className="text-slate-800">${getSubtotal().toFixed(2)}</span>
                  </div>
                  
                  {appliedDiscount && (
                    <div className="flex justify-between items-center text-green-600">
                      <span>Discount ({appliedDiscount.code}):</span>
                      <span>-${getDiscountAmount().toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Shipping:</span>
                    <span className="text-slate-800">
                      {getShippingCost() > 0 ? `$${getShippingCost().toFixed(2)}` : 'Free'}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center border-t border-slate-200 pt-2">
                    <span className="text-lg font-bold text-slate-800">Total:</span>
                    <span className="text-2xl font-bold text-blue-600">${getTotalPrice().toFixed(2)}</span>
                  </div>
                </div>
                
                <Button 
                  onClick={proceedToCheckout}
                  disabled={isCheckingOut}
                  className="w-full bg-blue-800 hover:bg-blue-900"
                  size="lg"
                >
                  {isCheckingOut ? 'Processing...' : 'Complete Order'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
